// PL3 (docs/build-briefs/PL3-payment-link-auto-check.md): the pure polling
// model that re-checks a sent payment link until it resolves. No DOM, no
// network, no clock of its own: the caller injects `check` and (in tests)
// the timers, so cadence, backoff and every stop condition are testable
// with a fake clock.
//
// It carries only what a check needs to say (an outcome), never a link
// URL, amount or any card data.

export type PaymentLinkCheckOutcome = "pending" | "resolved" | "error" | "stop";

/** Why a poll ended. A stop is always reported, never silent, so the
 * screen can tell the bartender to tap Check status instead. */
export type PaymentLinkPollStopReason = "resolved" | "cap" | "errors" | "stopped";

export interface PaymentLinkPollerConfig {
  /** Wait before the first check. */
  readonly initialDelayMs: number;
  /** Each later wait is the previous one times this. */
  readonly backoffFactor: number;
  /** No wait is ever longer than this. */
  readonly maxDelayMs: number;
  /** The poll ends after this many checks. */
  readonly maxChecks: number;
  /** The poll ends after this many consecutive `error` outcomes. */
  readonly maxConsecutiveErrors: number;
}

/** 15 s, 22.5 s, 33.75 s, 50.6 s, then 60 s; 40 checks is about 38 minutes. */
export const DEFAULT_PAYMENT_LINK_POLL_CONFIG: PaymentLinkPollerConfig = {
  initialDelayMs: 15_000,
  backoffFactor: 1.5,
  maxDelayMs: 60_000,
  maxChecks: 40,
  maxConsecutiveErrors: 3,
};

export interface PaymentLinkPollerTimers {
  setTimeout(callback: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

const REAL_TIMERS: PaymentLinkPollerTimers = {
  setTimeout: (callback, ms) => globalThis.setTimeout(callback, ms),
  clearTimeout: (handle) => globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>),
};

export interface PaymentLinkPollerOptions {
  check: () => Promise<PaymentLinkCheckOutcome>;
  onStop: (reason: PaymentLinkPollStopReason) => void;
  config?: PaymentLinkPollerConfig;
  timers?: PaymentLinkPollerTimers;
}

export interface PaymentLinkPoller {
  /** Begins the loop. Calling it while running does nothing. */
  start(): void;
  /** Ends the loop and clears the pending timer. Idempotent. A check that
   * is already in flight finishes but its outcome is ignored. */
  stop(): void;
  isRunning(): boolean;
  /** True while a check is awaiting its response. */
  isChecking(): boolean;
  /** Checks completed so far in this run. */
  checkCount(): number;
}

export function delayBeforeCheck(config: PaymentLinkPollerConfig, checkIndex: number): number {
  const raw = config.initialDelayMs * Math.pow(config.backoffFactor, checkIndex);
  return Math.min(raw, config.maxDelayMs);
}

export function createPaymentLinkPoller(options: PaymentLinkPollerOptions): PaymentLinkPoller {
  const config = options.config ?? DEFAULT_PAYMENT_LINK_POLL_CONFIG;
  const timers = options.timers ?? REAL_TIMERS;
  let running = false;
  // The run id of the check now awaiting its response, if any. Scoped to a
  // run so an abandoned check never blocks the next run's first tick.
  let checkingRun: number | undefined;
  let checks = 0;
  let consecutiveErrors = 0;
  let timer: unknown;
  // Bumped on every start and stop so an in-flight check from an earlier
  // run can never write into a later one.
  let runId = 0;

  function end(reason: PaymentLinkPollStopReason): void {
    if (!running) {
      return;
    }
    running = false;
    runId += 1;
    if (timer !== undefined) {
      timers.clearTimeout(timer);
      timer = undefined;
    }
    options.onStop(reason);
  }

  function arm(): void {
    timer = timers.setTimeout(
      () => {
        timer = undefined;
        void tick();
      },
      delayBeforeCheck(config, checks),
    );
  }

  async function tick(): Promise<void> {
    if (!running || checkingRun === runId) {
      return;
    }
    const myRun = runId;
    checkingRun = myRun;
    let outcome: PaymentLinkCheckOutcome;
    try {
      outcome = await options.check();
    } catch {
      outcome = "error";
    } finally {
      if (checkingRun === myRun) {
        checkingRun = undefined;
      }
    }
    if (!running || myRun !== runId) {
      return;
    }
    checks += 1;
    if (outcome === "resolved") {
      end("resolved");
      return;
    }
    if (outcome === "stop") {
      end("stopped");
      return;
    }
    consecutiveErrors = outcome === "error" ? consecutiveErrors + 1 : 0;
    if (consecutiveErrors >= config.maxConsecutiveErrors) {
      end("errors");
      return;
    }
    if (checks >= config.maxChecks) {
      end("cap");
      return;
    }
    // The next wait is armed only now, after this check settled, so two
    // checks can never be in flight together.
    arm();
  }

  return {
    start(): void {
      if (running) {
        return;
      }
      running = true;
      runId += 1;
      checks = 0;
      consecutiveErrors = 0;
      arm();
    },
    stop(): void {
      end("stopped");
    },
    isRunning: () => running,
    isChecking: () => checkingRun === runId,
    checkCount: () => checks,
  };
}
